import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_ABBREVIATIONS = DAYS.reduce((acc, day) => {
  acc[day.slice(0, 3)] = day;
  return acc;
}, {});
const formatTimeOption = (hour, minute) => {
  const period = hour < 12 ? "AM" : "PM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${period}`;
};
const TIMES = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2);
  const minute = index % 2 === 0 ? "00" : "30";
  return formatTimeOption(hour, minute);
});

const emptySchedule = () =>
  DAYS.reduce((acc, day) => {
    acc[day] = { open: false, from: "9:00 AM", to: "5:00 PM" };
    return acc;
  }, {});

const parseHours = (hours) => {
  if (!hours || typeof hours === "string") {
    const schedule = emptySchedule();

    if (typeof hours === "string") {
      hours.split(",").forEach((entry) => {
        const [dayLabel, timeRange] = entry.split(": ");
        const day = DAY_ABBREVIATIONS[dayLabel?.trim()];
        const [from, to] = (timeRange || "").split(" - ");

        if (day && from && to) {
          schedule[day] = { open: true, from: from.trim(), to: to.trim() };
        }
      });
    }

    return schedule;
  }
  if (typeof hours === "object" && !Array.isArray(hours)) {
    // Already structured - fill in any missing days
    const schedule = emptySchedule();
    for (const day of DAYS) {
      if (hours[day]) {
        schedule[day] = { ...schedule[day], ...hours[day] };
      }
    }
    return schedule;
  }
  return emptySchedule();
};

export const HoursSelector = ({ value, onChange }) => {
  const [schedule, setSchedule] = useState(() => parseHours(value));

  useEffect(() => {
    setSchedule(parseHours(value));
  }, [value]);

  const handleToggle = (day, checked) => {
    const updated = { ...schedule, [day]: { ...schedule[day], open: checked } };
    setSchedule(updated);
    onChange(updated);
  };

  const handleTimeChange = (day, field, val) => {
    const updated = { ...schedule, [day]: { ...schedule[day], [field]: val } };
    setSchedule(updated);
    onChange(updated);
  };

  return (
    <div className="space-y-3" data-testid="hours-selector">
      {DAYS.map((day) => (
        <div key={day} className="grid grid-cols-1 gap-2 py-2 sm:grid-cols-[8rem_minmax(0,1fr)] sm:items-center sm:gap-3">
          <div className="flex items-center gap-2 sm:w-32 sm:shrink-0">
            <Switch
              checked={schedule[day].open}
              onCheckedChange={(checked) => handleToggle(day, checked)}
              data-testid={`hours-toggle-${day.toLowerCase()}`}
            />
            <Label className={`text-sm font-medium ${schedule[day].open ? "text-[#1a1a1a]" : "text-gray-400"}`}>
              {day.slice(0, 3)}
            </Label>
          </div>
          {schedule[day].open ? (
            <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
              <Select value={schedule[day].from} onValueChange={(v) => handleTimeChange(day, "from", v)}>
                <SelectTrigger className="h-9 w-full min-w-0 text-xs" data-testid={`hours-from-${day.toLowerCase()}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-xs text-gray-400">to</span>
              <Select value={schedule[day].to} onValueChange={(v) => handleTimeChange(day, "to", v)}>
                <SelectTrigger className="h-9 w-full min-w-0 text-xs" data-testid={`hours-to-${day.toLowerCase()}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <span className="text-xs text-gray-400">Closed</span>
          )}
        </div>
      ))}
    </div>
  );
};

export const formatHoursDisplay = (hours) => {
  if (!hours || typeof hours === "string") return hours || "";
  if (typeof hours !== "object") return "";
  return DAYS
    .filter((d) => hours[d]?.open)
    .map((d) => `${d.slice(0, 3)}: ${hours[d].from} - ${hours[d].to}`)
    .join(", ");
};
